import React from 'react';

import { Autorenew as AutorenewIcon, Close as CloseIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import { Field } from 'react-final-form';
import { z } from 'zod';

import { Form } from '@hrlogics/react-components/form';

import {
  ActiveInactive,
  type IntegrationInfoFragment,
  IntegrationScope,
  IntegrationsListDocument,
  useIntegrationRegenerateCredentialsMutation,
  useIntegrationUpdateMutation,
} from 'src/api';
import { Modal } from 'src/components/common';
import { TextField } from 'src/components/finalForm';
import { useNotification } from 'src/hooks/app';
import { useModalV2 } from 'src/hooks/shared';
import { ModalV2Props } from 'src/types/modalV2';
import { SxProp } from 'src/types/mui';
import {
  msToRateLimitDuration,
  type RateLimitUnit,
  rateLimitDurationToMs,
} from 'src/utils/integrations';

import { CredentialsView } from './components/CredentialsView';
import { IntegrationVerifierField } from './components/IntegrationVerifierField';
import { RateLimitField } from './components/RateLimitField';
import { ScopeCardsField } from './components/ScopeCardField';
import { helperTextStyles, sectionLabelStyles } from './styles';

type VerifierOption = { id: string; displayName?: string | null };

type FormValues = {
  name: string;
  scope: IntegrationScope[];
  status: ActiveInactive;
  verifier: VerifierOption | null;
  ipLimitAttempts: number | null;
  ipLimitWindowValue: number | null;
  ipLimitWindowUnit: RateLimitUnit;
  credentialsLimitAttempts: number | null;
  credentialsLimitWindowValue: number | null;
  credentialsLimitWindowUnit: RateLimitUnit;
  verifierLimitAttempts: number | null;
  verifierLimitWindowValue: number | null;
  verifierLimitWindowUnit: RateLimitUnit;
};

type ManageIntegrationModalProps = {
  integration: IntegrationInfoFragment;
};

type Phase =
  | { kind: 'manage' }
  | { kind: 'confirm-regenerate' }
  | { kind: 'credentials'; clientId: string; clientSecret: string };

const rateLimitField = z
  .number({ error: 'Must be a non-negative integer' })
  .int('Must be an integer')
  .min(0, 'Must be 0 or greater')
  .nullable();

const validationSchema = z.object({
  name: z.string('Name is required').trim().nonempty('Name is required'),
  scope: z.array(z.enum(IntegrationScope)).min(1, 'At least one scope must be enabled'),
  status: z.enum(ActiveInactive),
  verifier: z.object({ id: z.string() }).nullable(),
  ipLimitAttempts: rateLimitField,
  ipLimitWindowValue: rateLimitField,
  ipLimitWindowUnit: z.enum(['sec', 'min', 'hour']),
  credentialsLimitAttempts: rateLimitField,
  credentialsLimitWindowValue: rateLimitField,
  credentialsLimitWindowUnit: z.enum(['sec', 'min', 'hour']),
  verifierLimitAttempts: rateLimitField,
  verifierLimitWindowValue: rateLimitField,
  verifierLimitWindowUnit: z.enum(['sec', 'min', 'hour']),
});

export const ManageIntegrationModal: React.FC<ModalV2Props<ManageIntegrationModalProps>> = ({
  integration,
  id,
}) => {
  const { closeModal, switchClosability } = useModalV2();
  const { createApolloContext } = useNotification();

  const [phase, setPhase] = React.useState<Phase>({ kind: 'manage' });

  const [updateIntegration] = useIntegrationUpdateMutation({
    context: createApolloContext({
      errorMessage: 'Failed to update integration',
      successMessage: 'Integration updated',
    }),
    refetchQueries: [IntegrationsListDocument],
  });

  const [regenerateCredentials, { loading: isRegenerating }] =
    useIntegrationRegenerateCredentialsMutation({
      context: createApolloContext({
        errorMessage: 'Failed to regenerate credentials',
      }),
      refetchQueries: [IntegrationsListDocument],
    });

  const onClose = React.useCallback(() => {
    closeModal(id);
    setPhase({ kind: 'manage' });
  }, [closeModal, id]);

  const initialValues = React.useMemo<FormValues | undefined>(() => {
    if (!integration) return undefined;
    const ip = msToRateLimitDuration(integration.ipLimitWindow);
    const creds = msToRateLimitDuration(integration.credentialsLimitWindow);
    const verifier = msToRateLimitDuration(integration.verifierLimitWindow);
    return {
      name: integration.name,
      scope: integration.scope,
      status: integration.status,
      verifier: integration.verifier ?? null,
      ipLimitAttempts: integration.ipLimitAttempts,
      ipLimitWindowValue: ip.value,
      ipLimitWindowUnit: ip.unit,
      credentialsLimitAttempts: integration.credentialsLimitAttempts,
      credentialsLimitWindowValue: creds.value,
      credentialsLimitWindowUnit: creds.unit,
      verifierLimitAttempts: integration.verifierLimitAttempts,
      verifierLimitWindowValue: verifier.value,
      verifierLimitWindowUnit: verifier.unit,
    };
  }, [integration]);

  const onSubmit = React.useCallback(
    async (values: FormValues) => {
      if (!integration) return;
      switchClosability(false);
      try {
        await updateIntegration({
          variables: {
            id: integration.id,
            name: values.name.trim(),
            scope: values.scope,
            status: values.status,
            ipLimitAttempts: values.ipLimitAttempts ?? 0,
            ipLimitWindow: rateLimitDurationToMs(
              values.ipLimitWindowValue ?? 0,
              values.ipLimitWindowUnit,
            ),
            credentialsLimitAttempts: values.credentialsLimitAttempts ?? 0,
            credentialsLimitWindow: rateLimitDurationToMs(
              values.credentialsLimitWindowValue ?? 0,
              values.credentialsLimitWindowUnit,
            ),
            verifierLimitAttempts: values.verifierLimitAttempts ?? 0,
            verifierLimitWindow: rateLimitDurationToMs(
              values.verifierLimitWindowValue ?? 0,
              values.verifierLimitWindowUnit,
            ),
          },
        });
        onClose();
      } finally {
        switchClosability(true);
      }
    },
    [integration, onClose, switchClosability, updateIntegration],
  );

  const onConfirmRegenerate = React.useCallback(async () => {
    if (!integration) return;
    switchClosability(false);
    try {
      const { data } = await regenerateCredentials({ variables: { id: integration.id } });
      if (data?.integrationRegenerateCredentials) {
        setPhase({
          kind: 'credentials',
          clientId: data.integrationRegenerateCredentials.clientId,
          clientSecret: data.integrationRegenerateCredentials.clientSecret,
        });
      }
    } catch {
      setPhase({ kind: 'manage' });
    } finally {
      switchClosability(true);
    }
  }, [integration, regenerateCredentials, switchClosability]);

  const closeButton = React.useMemo(
    () => ({
      show: true,
      actionName: 'close',
      onAction: onClose,
      render: () => (
        <IconButton onClick={onClose} aria-label="close">
          <CloseIcon />
        </IconButton>
      ),
    }),
    [onClose],
  );

  if (!integration || !initialValues) {
    return null;
  }

  if (phase.kind === 'credentials') {
    return (
      <Modal
        title={integration.name}
        titleProps={{ component: 'div' }}
        topAction={closeButton}
        dialogProps={{ open: true, onClose }}
      >
        <CredentialsView
          alertTitle="Copy and share these now — you won't see them again"
          alertBody={
            <React.Fragment>
              The previous credentials are revoked. Send the new <strong>client ID</strong> and{' '}
              <strong>client secret</strong> securely to the partner — they'll need both to
              authenticate. If lost, you'll need to regenerate again.
            </React.Fragment>
          }
          clientId={phase.clientId}
          clientSecret={phase.clientSecret}
          onClose={onClose}
        />
      </Modal>
    );
  }

  if (phase.kind === 'confirm-regenerate') {
    return (
      <Modal
        title={`Regenerate credentials for ${integration.name}?`}
        topAction={closeButton}
        dialogProps={{ open: true, onClose }}
      >
        <Stack spacing={3}>
          <Typography variant="body2" color="text.secondary">
            The current <strong>client ID</strong> and <strong>secret</strong> will be revoked
            immediately. Any system using the previous credentials will start failing until updated.
            The new client secret will be shown <strong>only once</strong> — you'll need to share it
            with the partner.
          </Typography>

          <Box display="flex" gap={2} justifyContent="space-between" alignItems="center">
            <Button
              variant="text"
              onClick={() => setPhase({ kind: 'manage' })}
              loading={isRegenerating}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={onConfirmRegenerate}
              loading={isRegenerating}
            >
              Regenerate
            </Button>
          </Box>
        </Stack>
      </Modal>
    );
  }

  return (
    <Modal
      title={integration.name}
      titleProps={{ component: 'div' }}
      topAction={closeButton}
      dialogProps={{ open: true, onClose }}
    >
      <Form<FormValues>
        onSubmit={onSubmit}
        subscription={{ submitting: true, valid: true, dirty: true }}
        initialValues={initialValues}
        validationSchema={validationSchema}
      >
        {({ submitting, valid, dirty }) => (
          <Stack spacing={3}>
            <Typography variant="body2" color="text.secondary">
              Update the integration's display name, scope, or active status. Credentials can be
              rotated separately — the new client secret will be shown only once.
            </Typography>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionLabelStyles}>
                Integration name
              </Typography>
              <Field
                name="name"
                component={TextField}
                fullWidth
                disabled={submitting}
                helperText="Internal label used to identify this partner in usage logs."
              />
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionLabelStyles}>
                Verifier
              </Typography>
              <IntegrationVerifierField disabled />
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionLabelStyles}>
                Scope
              </Typography>
              <ScopeCardsField disabled={submitting} />
              <Typography variant="caption" color="text.secondary" sx={helperTextStyles}>
                Controls which verification types this partner can request.{' '}
                <strong>At least one</strong> must be enabled. Out-of-scope requests return{' '}
                <Box component="code" sx={inlineCodeStyles}>
                  403 insufficient_scope
                </Box>
              </Typography>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionLabelStyles}>
                Status
              </Typography>
              <Field<ActiveInactive> name="status" subscription={{ value: true }}>
                {({ input }) => (
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={input.value === ActiveInactive.Active}
                          onChange={(_, checked) =>
                            input.onChange(
                              checked ? ActiveInactive.Active : ActiveInactive.Inactive,
                            )
                          }
                          color="success"
                          disabled={submitting}
                        />
                      }
                      label={input.value === ActiveInactive.Active ? 'Active' : 'Inactive'}
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {input.value === ActiveInactive.Active
                        ? 'Accepting requests with these credentials.'
                        : 'Requests with these credentials will be rejected.'}
                    </Typography>
                  </Box>
                )}
              </Field>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionLabelStyles}>
                Rate limits
              </Typography>
              <Stack spacing={2.5}>
                <RateLimitField
                  prefix="ip"
                  title="Per client IP"
                  description="Limits requests coming from the same source IP. Use 0 for either field to disable."
                  disabled={submitting}
                />
                <RateLimitField
                  prefix="credentials"
                  title="Per integration credentials"
                  description="Limits total requests authenticated with these credentials."
                  disabled={submitting}
                />
                <RateLimitField
                  prefix="verifier"
                  title="Per verifier user"
                  description="Limits requests per verifier_user_id supplied in the body."
                  disabled={submitting}
                />
              </Stack>
            </Box>

            <Box>
              <Typography variant="overline" color="text.secondary" sx={sectionLabelStyles}>
                Credentials
              </Typography>
              <Box sx={credentialsBoxStyles}>
                <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
                  <Typography variant="caption" color="text.secondary">
                    Client ID
                  </Typography>
                  <Box component="code" sx={clientIdValueStyles}>
                    {integration.clientId}
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AutorenewIcon fontSize="small" />}
                  onClick={() => setPhase({ kind: 'confirm-regenerate' })}
                  disabled={submitting}
                >
                  Regenerate
                </Button>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={helperTextStyles}>
                Regenerating issues a new client ID and secret. The previous credentials are revoked
                immediately.
              </Typography>
            </Box>

            <Box display="flex" gap={2} justifyContent="space-between" alignItems="center">
              <Button variant="text" onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={!valid || submitting || !dirty}
                loading={submitting}
              >
                Save Changes
              </Button>
            </Box>
          </Stack>
        )}
      </Form>
    </Modal>
  );
};

const inlineCodeStyles: SxProp = theme => ({
  fontFamily: 'monospace',
  fontSize: '0.85em',
  px: 0.5,
  borderRadius: 0.5,
  backgroundColor: theme.palette.action.hover,
});

const credentialsBoxStyles: SxProp = theme => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 2,
  p: 1.5,
  borderRadius: 1,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.action.hover,
});

const clientIdValueStyles: SxProp = {
  fontFamily: 'monospace',
  fontSize: '0.875rem',
  px: 1,
  py: 0.5,
  borderRadius: 0.5,
  backgroundColor: 'background.paper',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
